import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { AppliedFilter, AppliedFiltersBar } from '@/components/DataTable/AppliedFiltersBar'
import { Filter, FilterControls } from '@/components/DataTable/FilterControls'
import {
  ExportCsvMenu,
  ExportCsvMenuItem,
  ExportJobMenuItem,
} from '@/components/ExportCsv/ExportCsvMenu'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMarkingFilter from '@/hooks/filters/useMarkingFilter'
import useMaterialDistributionChannelFilter from '@/hooks/filters/useMaterialDistributionChannelFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import { useHandleExportJobCreated } from '@/hooks/queries/crt/useExports'
import {
  pnlTableSearchSchema,
  useCreatePnlExportJobs,
  useActualsPnl,
  useForecastsPnl,
} from '@/hooks/queries/crt/usePnl'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { getClearedFilters, resetFiltersPanel, useAppliedFilters } from '@/lib/utils/filters'
import { createFileRoute } from '@tanstack/react-router'
import { parseISO } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerMilSwitch } from '../-components/PerMilSwitch'
import { useOpenCrtConfigure } from '@/hooks/crt/useOpenCrtConfigure'
import { CrtVersionComputingNote } from '../-components/CrtVersionComputingNote'
import { CrtVersionsFooter } from '../-components/CrtVersionsFooter'
import {
  buildKpiDashboardCsv,
  resolveKpiRows,
  toKpiDashboardCsvExport,
  volumesOf,
} from '../-components/kpi-dashboard-csv'
import { getPnlView, toPnlViewSearch, type PnlView } from '../-components/pnl-view'
import { PnlViewSelector } from '../-components/PnlViewSelector'
import { useAppliedConfigBadges } from '../-components/useAppliedConfigBadges'
import { PROFITS_AND_LOSS_TABLE_SECTIONS } from './-components/profits-and-loss-table-config'
import { ProfitsAndLossSummaryTable } from './-components/ProfitsAndLossSummaryTable'

export const Route = createFileRoute('/_auth/_modules/crt/kpi-reports/profits-and-loss-summary/')({
  component: RouteComponent,
  validateSearch: (search) => pnlTableSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonth } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { crtPeriod } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
  })

  const view = getPnlView(search)
  // Next year has no actuals yet: fetch forecast-only (`year=NY`) and skip the actuals query.
  const isNextYear = view === 'nextYear'
  // Actuals-only: every month actualised in the selected period's year, with no forecast — pick a December
  // period to read a closed year whole. The sandbox has nothing to configure.
  const isActualsOnly = view === 'actualsOnly'
  // The view is a URL param only, never the period: switching it keeps the period and the loaded version.
  const handleViewChange = (view: PnlView) => {
    navigate({ search: { ...search, ...toPnlViewSearch(view) } })
  }

  const { productCategoryFilter, productCategories, setProductCategories } =
    useProductCategoryFilter(search.productCategory)
  const { brandFilter, brands, setBrands } = useBrandFilter(search.brand)
  const { variantFilter, variants, setVariants } = useVariantFilter(search.variant)
  const { materialGroupFilter, materialGroups, setMaterialGroups } = useMaterialGroupFilter(
    search.materialGroupCode,
  )
  const { markingFilter, marking, setMarking } = useMarkingFilter(search.marking)
  const { productTypeFilter, productTypes, setProductTypes } = useProductTypeFilter(
    search.productType,
  )
  const { distributionChannelFilter, distributionChannels, setDistributionChannels } =
    useMaterialDistributionChannelFilter(search.distributionChannel)

  const filters = [
    productCategoryFilter,
    brandFilter,
    variantFilter,
    materialGroupFilter,
    markingFilter,
    productTypeFilter,
    distributionChannelFilter,
  ]

  const selectedFilters = {
    productCategory: productCategories?.length ? productCategories : undefined,
    brand: brands?.length ? brands : undefined,
    variant: variants?.length ? variants : undefined,
    materialGroupCode: materialGroups?.length ? materialGroups : undefined,
    marking: marking?.length ? marking : undefined,
    productType: productTypes?.length ? productTypes : undefined,
    distributionChannel: distributionChannels?.length ? distributionChannels : undefined,
  }

  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)

  // Currency is display-only and picked in the header: it never reaches the backend, so it stays out of
  // `appliedFilters`. Called after the version sync so both can mirror into the URL on arrival.
  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true }),
  })

  // The units switch sits in the toolbar and applies at once. The assumptions, investments and volume
  // scenario are changed from the header's configure dialog, which mirrors them into the URL.
  const handleUnitsChange = (isPerMil: boolean) => {
    navigate({ search: { ...search, isPerMil: isPerMil || undefined } })
  }
  const openConfigure = useOpenCrtConfigure()

  const appliedFilterBadges = useAppliedFilters(filters as Filter<unknown>[], search)
  // The sandbox stays in the URL through actuals-only so switching back restores it, but it applies to
  // neither the request nor the badges there.
  const sandbox = isActualsOnly
    ? {}
    : {
        pricingAssumptionIds: search.pricingAssumptionIds,
        taxAssumptionIds: search.taxAssumptionIds,
        manualInvestmentIds: search.manualInvestmentIds,
        // Forward the loaded version so the backend can serve its precomputed rows; the handler drops back to
        // a live compute when the selection has diverged from the version.
        loadedVersionId: search.loadedVersionId,
        volumeScenarioId: search.volumeScenarioId,
      }

  const appliedConfigBadges = useAppliedConfigBadges({
    pricingAssumptionIds: sandbox.pricingAssumptionIds,
    taxAssumptionIds: sandbox.taxAssumptionIds,
    manualInvestmentIds: sandbox.manualInvestmentIds,
    volumeScenarioId: sandbox.volumeScenarioId,
  })

  // Filters and configs share one bar; each badge opens where it is changed: the configuration in the header's
  // configure dialog, filters in the filters panel.
  const handleBadgeClick = (badge: AppliedFilter) => {
    if (appliedConfigBadges.some((config) => config.key === badge.key)) openConfigure()
    else setIsFiltersPanelOpen(true)
  }

  // Re-sync the panel's local state to the applied URL params whenever it opens, so an aborted edit
  // (open → change → close without applying) does not linger on the next open.
  const handleFiltersPanelOpenChange = () => {
    setIsFiltersPanelOpen((open) => {
      if (!open) resetFiltersPanel(filters as Filter<unknown>[], search)
      return !open
    })
  }

  const showResults = () => {
    navigate({ search: { ...search, ...selectedFilters } })
  }

  const resetAllFilters = () => {
    setProductCategories([])
    setBrands([])
    setVariants([])
    setMaterialGroups([])
    setMarking([])
    setProductTypes([])
    setDistributionChannels([])
    navigate({ search: { ...search, ...getClearedFilters(selectedFilters) } })
  }

  // The SKU-level exports run in the background and land in "My Exports"; a BPC feed with over-1M
  // per-unit values is flagged on its row there.
  const handleExportJobCreated = useHandleExportJobCreated()
  const { bpcFeed, exportView } = useCreatePnlExportJobs({
    crtPeriod,
    onJobCreated: handleExportJobCreated,
  })

  const appliedFilters = {
    productCategory: search.productCategory,
    brand: search.brand,
    variant: search.variant,
    materialGroupCode: search.materialGroupCode,
    marking: search.marking,
    productType: search.productType,
    distributionChannel: search.distributionChannel,
    isPerMil: search.isPerMil,
    year: search.year,
    // Without the sandbox, actuals-only reads its precomputed full-year slice rather than a live compute.
    actualsOnly: isActualsOnly || undefined,
    ...sandbox,
  }

  const {
    data: actualsData,
    isPending: isActualsPending,
    error: actualsError,
  } = useActualsPnl(crtPeriod, appliedFilters, { enabled: !isNextYear })
  const {
    data: forecastsData,
    isPending: isForecastsPending,
    error: forecastsError,
  } = useForecastsPnl(crtPeriod, appliedFilters, { enabled: !isActualsOnly })

  // Keep the skeletons until BOTH queries resolve — otherwise the faster one clears them while the
  // other's cells are still empty. In next-year mode the actuals query is disabled (stays `pending`
  // forever), and in actuals-only mode the forecasts query is, so ignore the disabled one.
  const isPending = (!isActualsOnly && isForecastsPending) || (!isNextYear && isActualsPending)
  const error = isNextYear ? forecastsError : actualsError
  const months = (isNextYear ? forecastsData : actualsData)?.meta.months ?? []

  // The table as displayed, built from the same loaded trees the table reads.
  const dashboardRows = resolveKpiRows(
    PROFITS_AND_LOSS_TABLE_SECTIONS,
    isNextYear ? undefined : actualsData?.breakdown,
    isActualsOnly ? undefined : forecastsData?.breakdown,
  )
  const exportDashboard = () =>
    Promise.resolve(
      toKpiDashboardCsvExport(
        buildKpiDashboardCsv({
          rows: dashboardRows,
          monthLabels: months.map((month) => formatMonth(parseISO(month), { month: 'short' })),
          labelOf: (key) => t(`kpis.labels.${key}`),
          headers: {
            description: t('kpiReports.columns.description'),
            fullYearTotal: t('kpiReports.columns.fullYearTotal'),
          },
          currency: conversion,
          perMilVolumes: search.isPerMil
            ? volumesOf(
                isNextYear ? undefined : actualsData?.breakdown,
                isActualsOnly ? undefined : forecastsData?.breakdown,
              )
            : undefined,
        }),
        'profits-and-loss-summary',
      ),
    )

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <Spread>
        <PageHeader>{t('kpiReports.profitsAndLossSummary.pageHeader')}</PageHeader>
        <VStack gap="md" align="end">
          <HStack gap="sm">
            <PnlViewSelector value={view} onChange={handleViewChange} />
            <PerMilSwitch isPerMil={!!search.isPerMil} onChange={handleUnitsChange} />
            <FilterControls
              filters={filters as Filter<unknown>[]}
              activeFilterCount={appliedFilterBadges.length}
              isOpen={isFiltersPanelOpen}
              onOpenChange={handleFiltersPanelOpenChange}
              onShowResults={showResults}
              onResetAllFilters={resetAllFilters}
            />
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('kpiReports.export.dashboard')}
                  description={t('kpiReports.export.dashboardDescription')}
                  getCsvExport={exportDashboard}
                  rowCount={isPending ? 0 : dashboardRows.length}
                />,
                // The SKU exports report the year-to-date + forecast build; the actuals-only view has no such file.
                ...(isActualsOnly
                  ? []
                  : [
                      <ExportJobMenuItem
                        label={t('export.viewData', { ns: 'common' })}
                        description={t(
                          'kpiReports.profitsAndLossSummary.export.materialsDescription',
                        )}
                        // Mirror what the table sends so the file matches the on-screen view: the raw
                        // assumption ids fully describe it (including a layered selection).
                        startJob={() =>
                          exportView.mutateAsync({
                            pricingAssumptionIds: search.pricingAssumptionIds,
                            taxAssumptionIds: search.taxAssumptionIds,
                            manualInvestmentIds: search.manualInvestmentIds,
                          })
                        }
                      />,
                      <ExportJobMenuItem
                        label={t('kpiReports.profitsAndLossSummary.export.domestic')}
                        description={t(
                          'kpiReports.profitsAndLossSummary.export.domesticDescription',
                        )}
                        startJob={() => bpcFeed.mutateAsync('domestic')}
                      />,
                      <ExportJobMenuItem
                        label={t('kpiReports.profitsAndLossSummary.export.cdf')}
                        description={t('kpiReports.profitsAndLossSummary.export.cdfDescription')}
                        startJob={() => bpcFeed.mutateAsync('export')}
                      />,
                    ]),
              ]}
            />
          </HStack>
        </VStack>
      </Spread>
      <CrtVersionComputingNote crtPeriod={crtPeriod} versionId={search.loadedVersionId} />

      <Card className="w-full">
        <CardContent className="pt-6">
          {/* Same card as the table so the bar reads on white, as on the detail tables. */}
          <VStack gap="sm" align="stretch">
            <AppliedFiltersBar
              label={t('assumptionsConfig.appliedLabel')}
              appliedFilters={[...appliedFilterBadges, ...appliedConfigBadges]}
              onBadgeClick={handleBadgeClick}
            />
            <ProfitsAndLossSummaryTable
              sections={PROFITS_AND_LOSS_TABLE_SECTIONS}
              actuals={isNextYear ? undefined : actualsData?.breakdown}
              forecast={isActualsOnly ? undefined : forecastsData?.breakdown}
              months={months}
              crtPeriod={crtPeriod}
              search={search}
              isNextYear={isNextYear}
              isActualsOnly={isActualsOnly}
              isPending={isPending}
              error={error}
              currency={conversion}
              isPerMil={!!search.isPerMil}
            />
          </VStack>
        </CardContent>
      </Card>

      <CrtVersionsFooter />
    </PageContainer>
  )
}
